import React, { useEffect, useState } from 'react';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, X, ImageOff } from 'lucide-react';
import { fetchPrivateFile } from '../utils/privateFile';

const useSignedUrls = (photos) => {
  const [urls, setUrls] = useState({});
  const ids = photos.map((p) => p.id).join(',');
  useEffect(() => {
    let alive = true;
    const made = [];
    photos.forEach((p) => fetchPrivateFile(`/admin/kyc/documents/${p.id}/file`, { preview: true })
      .then((url) => { made.push(url); if (alive) setUrls((u) => ({ ...u, [p.id]: url })); })
      .catch(() => alive && setUrls((u) => ({ ...u, [p.id]: '' }))));
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [ids]);
  return urls;
};

const Lightbox = ({ photos, urls, index, setIndex, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [origin, setOrigin] = useState('50% 50%');
  const [broken, setBroken] = useState({});
  const p = photos[index];
  useEffect(() => { setZoom(1); }, [index]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % photos.length);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose, setIndex]);
  const originAt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const toggleAt = (e) => { e.stopPropagation(); originAt(e); setZoom((z) => (z > 1 ? 1 : 2.5)); };
  const stop = (e) => e.stopPropagation();
  const btn = 'p-2 rounded-full bg-gray-900/80 border border-gray-700 text-white hover:bg-gray-800 transition-colors';
  return (
    <div className="fixed inset-0 z-[1200] bg-black/90 backdrop-blur-md flex flex-col" data-testid="visit-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between p-4 text-sm text-gray-300">
        <span data-testid="visit-lightbox-caption">{p.original_filename} · {new Date(p.created_at).toLocaleString('es-PE')} · {index + 1}/{photos.length}</span>
        <div className="flex gap-2" onClick={stop}>
          <button className={btn} onClick={() => setZoom((z) => Math.max(1, z - 0.5))} data-testid="visit-lightbox-zoom-out"><ZoomOut className="w-4 h-4" /></button>
          <button className={btn} onClick={() => setZoom((z) => Math.min(5, z + 0.5))} data-testid="visit-lightbox-zoom-in"><ZoomIn className="w-4 h-4" /></button>
          <button className={btn} onClick={onClose} data-testid="visit-lightbox-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-hidden px-16 pb-8 relative">
        {photos.length > 1 && <button className={`${btn} absolute left-4`} onClick={(e) => stop(e) || setIndex((i) => (i - 1 + photos.length) % photos.length)} data-testid="visit-lightbox-prev"><ChevronLeft className="w-5 h-5" /></button>}
        {urls[p.id] && !broken[p.id]
          ? <img src={urls[p.id]} alt={p.original_filename} onClick={toggleAt} onMouseMove={(e) => zoom > 1 && originAt(e)}
              onError={() => setBroken((b) => ({ ...b, [p.id]: true }))} data-testid="visit-lightbox-image"
              style={{ transform: `scale(${zoom})`, transformOrigin: origin }}
              className={`max-h-full max-w-full object-contain transition-transform duration-200 ${zoom > 1 ? 'cursor-zoom-out' : 'cursor-zoom-in'}`} />
          : <div className="flex flex-col items-center gap-2 text-gray-500 text-sm" onClick={stop} data-testid="visit-lightbox-broken"><ImageOff className="w-10 h-10" /> No se pudo mostrar la imagen</div>}
        {photos.length > 1 && <button className={`${btn} absolute right-4`} onClick={(e) => stop(e) || setIndex((i) => (i + 1) % photos.length)} data-testid="visit-lightbox-next"><ChevronRight className="w-5 h-5" /></button>}
      </div>
    </div>
  );
};

const Thumb = ({ p, url, onOpen }) => {
  const [bad, setBad] = useState(false);
  return (
    <button onClick={onOpen} title={p.original_filename} data-testid={`site-visit-evidence-view-${p.id}`}
      className="w-20 h-20 rounded-lg overflow-hidden border border-gray-700 hover:border-[#FF6B35] bg-gray-900 flex items-center justify-center transition-colors">
      {url && !bad ? <img src={url} alt={p.original_filename} className="w-full h-full object-cover" loading="lazy" onError={() => setBad(true)} />
        : url === '' || bad ? <span className="flex flex-col items-center text-[10px] text-gray-500" title="Imagen no disponible"><ImageOff className="w-5 h-5" />No disponible</span> : <div className="w-full h-full animate-pulse bg-gray-800" />}
    </button>
  );
};

export const VisitGallery = ({ photos }) => {
  const urls = useSignedUrls(photos);
  const [open, setOpen] = useState(null);
  if (!photos.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="visit-gallery">
        {photos.map((p, i) => <Thumb key={p.id} p={p} url={urls[p.id]} onOpen={() => setOpen(i)} />)}
      </div>
      {open !== null && <Lightbox photos={photos} urls={urls} index={open} setIndex={setOpen} onClose={() => setOpen(null)} />}
    </>
  );
};

export default VisitGallery;
